import React, { useState, useCallback, useEffect } from 'react';
import { FormControl } from 'react-bootstrap';
import styles from './EditableTermField.module.scss';

interface EditableTermFieldProps {
  label: string;
  value: string | boolean | null | undefined;
  onSave: (newValue: string) => Promise<void>;
  disabled?: boolean;
  type?: 'text' | 'date' | 'number' | 'textarea' | 'boolean';
  currentlyEditingField: string | null;
  onStartEdit: (fieldKey: string) => void;
  fieldKey: string;
}

export const EditableTermField: React.FC<EditableTermFieldProps> = ({
  label,
  value,
  onSave,
  disabled = false,
  type = 'text',
  currentlyEditingField,
  onStartEdit,
  fieldKey,
}) => {
  const isEditing = currentlyEditingField === fieldKey;
  // Convert boolean to string for field value state
  const initialValue = typeof value === 'boolean' ? String(value) : (value || '');
  const [fieldValue, setFieldValue] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset field value when value prop changes or editing state changes
  useEffect(() => {
    const newValue = typeof value === 'boolean' ? String(value) : (value || '');
    setFieldValue(newValue);
  }, [value, isEditing]);

  const hasChanges = fieldValue !== initialValue;

  const handleStartEdit = useCallback(() => {
    if (disabled) return;
    onStartEdit(fieldKey);
  }, [disabled, onStartEdit, fieldKey]);

  const handleSave = useCallback(async () => {
    if (!hasChanges) {
      onStartEdit(''); // Exit edit mode
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave(fieldValue);
      onStartEdit(''); // Exit edit mode on success
    } catch (err) {
      console.error('Failed to save field:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [fieldValue, hasChanges, onSave, onStartEdit]);

  const handleCancel = useCallback(() => {
    const resetValue = typeof value === 'boolean' ? String(value) : (value || '');
    setFieldValue(resetValue);
    setError(null);
    onStartEdit(''); // Exit edit mode
  }, [value, onStartEdit]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // For textarea, only save on Cmd/Ctrl+Enter
    if (type === 'textarea') {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    } else {
      // For other fields, save on Enter
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    }
  }, [handleSave, handleCancel, type]);

  const formatDisplayValue = (val: string | boolean | null | undefined): string => {
    // Handle boolean values
    if (typeof val === 'boolean') {
      return val ? 'Yes' : 'No';
    }

    if (!val) return '—';

    // Format dates for display.
    // Date-only strings ("YYYY-MM-DD") must be parsed as LOCAL midnight, not UTC
    // midnight — otherwise they render as the previous day for viewers west of
    // UTC (e.g. "2026-01-01" -> "December 31, 2025" in US timezones).
    if (type === 'date' && val) {
      try {
        const dateStr = String(val);
        const localValue = /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
          ? `${dateStr}T00:00:00`
          : dateStr;
        const date = new Date(localValue);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch {
        return val;
      }
    }

    return val;
  };

  return (
    <div className={styles.termField}>
      <div className={styles.labelRow}>
        <div className={styles.label}>{label}</div>
        {isEditing && (
          <div className={styles.actionButtons}>
            <button
              className={styles.saveButton}
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              aria-label="Save"
            >
              Save
            </button>
            <button
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={isSaving}
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {!isEditing ? (
        <div
          className={`${styles.displayMode} ${!disabled ? styles.clickable : ''}`}
          onClick={handleStartEdit}
        >
          <span className={styles.value}>{formatDisplayValue(value)}</span>
          {!disabled && (
            <div className={styles.editOverlay}>
              <span className={styles.editOverlayText}>Edit</span>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.editMode}>
          {type === 'boolean' ? (
            <FormControl
              as="select"
              value={fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSaving}
              isInvalid={!!error}
              autoFocus
              className={styles.input}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </FormControl>
          ) : type === 'textarea' ? (
            <FormControl
              as="textarea"
              rows={4}
              value={fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSaving}
              isInvalid={!!error}
              autoFocus
              className={styles.textarea}
            />
          ) : (
            <FormControl
              type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
              value={fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSaving}
              isInvalid={!!error}
              autoFocus
              className={styles.input}
            />
          )}
          {error && <div className={styles.error}>{error}</div>}
        </div>
      )}
    </div>
  );
};
